/**
 * Courses API Service
 * Handles all API calls for published courses
 *
 * Uses shared API client with automatic token refresh for session extension
 */

import { apiClient as api } from './apiClient';
import type {
  ComprehensiveVocabularyEntry,
  AnnotatedSentence,
  PhraseEntry,
  FullTextTranslation,
  QuizQuestion,
} from '../types/creator-workflow';

import { API_BASE_URL } from '../config';
const COURSES_API_BASE = `${API_BASE_URL}/api/v1/courses`;

// Permission types
export type CoursePermission = 'public' | 'private' | 'followers' | 'group';

export interface CourseWhitelistEntry {
  id: string;
  courseId: string;
  email: string;
  createdAt: string;
}

export interface CreatorGroup {
  id: string;
  creatorId: string;
  name: string;
  description?: string;
  memberCount?: number;
  createdAt: string;
  updatedAt: string;
}

// Types for Course entities
export interface Course {
  id: string;
  title: string;
  titles?: Record<string, string>;
  description: string;
  audioUrl?: string;
  transcript?: string;
  status: 'draft' | 'review' | 'published' | 'archived';
  permission?: CoursePermission;
  scheduledPublishAt?: string;
  durationSeconds?: number;
  wordCount?: number;
  difficultyLevel?: string;
  cefrLevel?: string;
  coverImageUrl?: string;
  thumbnailUrl?: string;
  tags?: string[];
  enrollmentCount?: number;
  rating?: number; // Alias for ratingAverage (for backward compatibility)
  ratingAverage?: number;
  ratingCount?: number; // Number of ratings received
  translationLanguage?: string;
  creatorId: string;
  creator?: {
    id: string;
    fullName?: string;
    username?: string;
    email?: string;
  };
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  // Subtitle URLs for bilingual display
  subtitleUrlEn?: string;
  subtitleUrlNative?: string;
  // Enriched data from source draft
  vocabularyCount?: number;
  phraseCount?: number;
  sentenceCount?: number;
  // Topic classification
  topics?: Array<{
    code: string;
    labels: Record<string, string>;
    relevanceScore: number;
    rank: number;
  }>;
  // Speaking speed in words per minute (calculated from SRT timings)
  speakingWpm?: number;
  // Accent + content type (stored on course row, separate from topic knowledge graph)
  accent?: string;
  contentType?: 'dialogue' | 'article' | 'news' | 'speech' | 'advertisement' | 'story';
}

export interface Section {
  id: string;
  courseId: string;
  title: string;
  description?: string;
  orderIndex: number;
  isPublished: boolean;
  episodes: Episode[];
}

export interface Episode {
  id: string;
  sectionId: string;
  title: string;
  description?: string;
  audioUrl?: string;
  durationSeconds?: number;
  orderIndex: number;
  segments: Segment[];
}

export interface Segment {
  id: string;
  episodeId: string;
  index: number;
  startTime: number;
  endTime: number;
  speaker?: string;
  text: string;
  translation?: string;
  translations?: Record<string, string> | null;
  highlightedWords?: any;
  wordTimestamps?: Array<{
    word: string;
    start: number;
    end: number;
    confidence?: number;
  }> | null;
  // Sentence audio URL for individual playback
  audioUrl?: string;
}

// Legacy vocabulary (basic)
export interface Vocabulary {
  id: string;
  word: string;
  pronunciation?: string;
  definition?: string;
  definitionNative?: string;
  partOfSpeech?: string;
  examples?: string[];
  audioUrl?: string;
}

// Re-export comprehensive types for convenience
export type { ComprehensiveVocabularyEntry, AnnotatedSentence, PhraseEntry, FullTextTranslation, QuizQuestion };

export interface FullCourseData {
  course: Course;
  sections: Section[];
  vocabulary: Vocabulary[];
  // Comprehensive learning content (from AI parsing)
  sentences?: AnnotatedSentence[];
  comprehensiveVocabulary?: ComprehensiveVocabularyEntry[];
  phrases?: PhraseEntry[];
  fullTextTranslation?: FullTextTranslation;
  quizQuestions?: QuizQuestion[];
  // Subtitle URLs from source draft
  subtitleUrlEn?: string;
  subtitleUrlNative?: string;
  subtitleUrlsNative?: Record<string, string> | null; // Multi-language SRT URLs
  fullTextTranslations?: Record<string, string> | null; // Multi-language full text translations
  // Audio URL from source draft (fallback if course doesn't have one)
  draftAudioUrl?: string;
  // Source draft ID for editing content in creator mode
  sourceDraftId?: string;
  stats: {
    sectionCount: number;
    episodeCount: number;
    segmentCount: number;
    vocabularyCount: number;
  };
}

export interface UpdateSegmentData {
  text?: string;
  translation?: string;
  speaker?: string;
}

// Search and filter parameters
export interface SearchCoursesParams {
  q?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  cefrLevel?: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
  creatorId?: string;
  translationLanguage?: string;
  topic?: string;
  minRating?: number;
  minDuration?: number;
  maxDuration?: number;
  accent?: string;
  contentType?: 'dialogue' | 'article' | 'news' | 'speech' | 'advertisement' | 'story';
  visibility?: 'public' | 'private';
  sortBy?: 'newest' | 'popular' | 'rating' | 'duration';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface PaginatedCourses {
  courses: Course[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

// Filter options returned by the API
export interface TopicInfo {
  code: string;
  labels: Record<string, string>;
  icon: string | null;
  courseCount: number;
}

export interface CourseFilters {
  creators: Array<{
    id: string;
    fullName: string;
    username: string;
    courseCount: number;
  }>;
  difficultyLevels: Array<{
    level: string;
    count: number;
  }>;
  cefrLevels: Array<{
    level: string;
    count: number;
  }>;
  translationLanguages: Array<{
    language: string;
    count: number;
  }>;
  topics: TopicInfo[];
  accents: Array<{
    accent: string;
    count: number;
  }>;
  contentTypes: Array<{
    contentType: string;
    count: number;
  }>;
}

export const coursesService = {
  /**
   * Get filter options for courses
   */
  async getFilters(): Promise<CourseFilters> {
    const response = await api.get<{ success: boolean; data: CourseFilters }>(
      `${COURSES_API_BASE}/filters`,
    );
    return response.data.data;
  },

  /**
   * Get topic categories with course counts
   */
  async getTopics(): Promise<TopicInfo[]> {
    const response = await api.get<{ success: boolean; data: TopicInfo[] }>(
      `${COURSES_API_BASE}/topics`,
    );
    return response.data.data;
  },

  /**
   * Get published courses with optional search/filter and pagination
   */
  async getCourses(params?: SearchCoursesParams): Promise<PaginatedCourses> {
    const response = await api.get<{ success: boolean; data: Course[]; pagination: { total: number; limit: number; offset: number } }>(COURSES_API_BASE, {
      params,
    });
    return {
      courses: response.data.data,
      pagination: response.data.pagination,
    };
  },

  /**
   * Get a course by ID
   */
  async getCourse(courseId: string): Promise<Course> {
    const response = await api.get<{ success: boolean; data: Course }>(
      `${COURSES_API_BASE}/${courseId}`,
    );
    return response.data.data;
  },

  /**
   * Get full course content with sections, episodes, segments, and vocabulary
   */
  async getFullCourse(courseId: string): Promise<FullCourseData> {
    const response = await api.get<{ success: boolean; data: FullCourseData }>(
      `${COURSES_API_BASE}/${courseId}/full`,
    );
    return response.data.data;
  },

  /**
   * Distinct tags used across published courses (for autocomplete).
   */
  async listAllTags(): Promise<string[]> {
    const response = await api.get<{ success: boolean; data: string[] }>(
      `${COURSES_API_BASE}/tags/all`,
    );
    return response.data.data ?? [];
  },

  /**
   * Update a course
   */
  async updateCourse(courseId: string, data: Partial<Course>): Promise<Course> {
    const response = await api.patch<{ success: boolean; data: Course }>(
      `${COURSES_API_BASE}/${courseId}`,
      data,
    );
    return response.data.data;
  },

  /**
   * Update a segment in a course
   */
  async updateSegment(
    courseId: string,
    segmentId: string,
    data: UpdateSegmentData,
  ): Promise<Segment> {
    const response = await api.patch<{ success: boolean; data: Segment }>(
      `${COURSES_API_BASE}/${courseId}/segments/${segmentId}`,
      data,
    );
    return response.data.data;
  },

  /**
   * Delete a course (soft delete)
   */
  async deleteCourse(courseId: string): Promise<void> {
    await api.delete(`${COURSES_API_BASE}/${courseId}`);
  },

  /**
   * Generate AI thumbnail for a course
   */
  async generateThumbnail(
    courseId: string,
    options?: { style?: string },
  ): Promise<{ thumbnailUrl: string; estimatedCost: number }> {
    const response = await api.post<{
      success: boolean;
      data: { thumbnailUrl: string; estimatedCost: number };
    }>(`${COURSES_API_BASE}/${courseId}/generate-thumbnail`, options || {});
    return response.data.data;
  },

  // ─── Whitelist Management ──────────────────────────────────────

  async getWhitelist(courseId: string): Promise<CourseWhitelistEntry[]> {
    const response = await api.get<{ success: boolean; data: CourseWhitelistEntry[] }>(
      `${COURSES_API_BASE}/${courseId}/whitelist`,
    );
    return response.data.data;
  },

  async addToWhitelist(courseId: string, emails: string[]): Promise<{ added: string[]; duplicates: string[] }> {
    const response = await api.post<{ success: boolean; data: { added: string[]; duplicates: string[] } }>(
      `${COURSES_API_BASE}/${courseId}/whitelist`,
      { emails },
    );
    return response.data.data;
  },

  async removeFromWhitelist(courseId: string, email: string): Promise<void> {
    await api.delete(`${COURSES_API_BASE}/${courseId}/whitelist/${encodeURIComponent(email)}`);
  },

  // ─── Course-Group Linking ──────────────────────────────────────

  async getCourseGroups(courseId: string): Promise<CreatorGroup[]> {
    const response = await api.get<{ success: boolean; data: CreatorGroup[] }>(
      `${COURSES_API_BASE}/${courseId}/groups`,
    );
    return response.data.data;
  },

  async linkGroupsToCourse(courseId: string, groupIds: string[]): Promise<void> {
    await api.post(`${COURSES_API_BASE}/${courseId}/groups`, { groupIds });
  },

  async unlinkGroupFromCourse(courseId: string, groupId: string): Promise<void> {
    await api.delete(`${COURSES_API_BASE}/${courseId}/groups/${groupId}`);
  },
};

export default coursesService;
